import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';

import { queries } from 'RestAPIs/rapini';
import {
  ControlledAutoComplete,
  ControlledTextField
} from 'components/shared/ControlledFields';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import ProtocolRulePhase from './ProtocolRulePhase';
import { PhaseIconButton, PhaseList } from './ProtocolRuleForm.styles';
import ProtocolRuleSummary from './ProtocolRuleSummary';
import schema from './ProtocolRuleForm.schema';
import {
  describePhaseValues,
  newPhase,
  toProtocolRuleFormValues
} from './ProtocolRuleForm.utils';
import { useSaveProtocolRule } from './hooks/useSaveProtocolRule';

import type { ActivityResponse } from 'generated/index';
import type {
  ProtocolRuleFormProps,
  ProtocolRuleFormValues
} from './ProtocolRuleForm.types';

/**
 * A rule of a protocol: at the top a summary that reads along, then the activity, its course as one
 * or more phases, and remarks. Saving stays blocked until an activity is chosen; the other messages only
 * appear after a save attempt (react-hook-form's default `onSubmit` mode).
 */
const ProtocolRuleForm = ({
  protocolId,
  rule,
  onSaved
}: ProtocolRuleFormProps) => {
  'use memo';
  const { t } = useTranslation('common');
  const methods = useForm<ProtocolRuleFormValues>({
    defaultValues: toProtocolRuleFormValues(rule),
    resolver: yupResolver(schema)
  });
  const { control, handleSubmit, getValues } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: 'phases' });
  // The phases the form opened with; a phase added since then opens with its fields showing
  const [initialPhaseIds] = useState(
    () => new Set(fields.map((field) => field.id))
  );
  const activity = useWatch({ control, name: 'activity' });
  // The backend needs a scheduling label (without one it fails), and protocols are MRP master data.
  // The generated hook serialises an array as `schedulingLabels[]=…`, which the endpoint does not
  // read, so the label goes in as a plain string, as the other callers of this endpoint do.
  const { data: activities = [] } = queries.useGetActivities(
    // @ts-expect-error TODO: BE should accept array of ids
    'MRP'
  );
  const { saveRule, isSaving } = useSaveProtocolRule(protocolId, rule, onSaved);

  return (
    <MultiStepForm onSubmit={handleSubmit(saveRule)} methods={methods}>
      <ProtocolRuleSummary control={control} />
      <MultiStepForm.Section
        title={t('adminPortal.protocols.ruleForm.activity')}
      >
        <ControlledAutoComplete<ActivityResponse>
          control={control}
          name="activity"
          label={t('adminPortal.protocols.ruleForm.activity')}
          options={activities}
          idProperty="entityId"
          variant="outlined"
          fullWidth
        />
      </MultiStepForm.Section>
      <MultiStepForm.Section
        title={t('adminPortal.protocols.ruleForm.verloop')}
        action={
          <PhaseIconButton
            type="plus"
            size="small"
            aria-label={t('adminPortal.protocols.ruleForm.addPhase')}
            onClick={() => append(newPhase(getValues('phases').at(-1)))}
          />
        }
      >
        <PhaseList>
          {fields.map((field, index) => (
            <ProtocolRulePhase
              key={field.id}
              index={index}
              canRemove={fields.length > 1}
              onRemove={() => remove(index)}
              initiallyEditing={
                !initialPhaseIds.has(field.id) ||
                !describePhaseValues(getValues(`phases.${index}`))
              }
            />
          ))}
        </PhaseList>
      </MultiStepForm.Section>
      <MultiStepForm.Section
        title={t('adminPortal.protocols.ruleForm.description')}
      >
        <ControlledTextField
          control={control}
          name="description"
          label={t('adminPortal.protocols.ruleForm.description')}
          placeholder={t(
            'adminPortal.protocols.ruleForm.descriptionPlaceholder'
          )}
          variant="outlined"
          multiline
          minRows={2}
          fullWidth
        />
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isSaving}
          disabled={!activity}
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default ProtocolRuleForm;
